import { ReactElement, useMemo } from 'react';
import { Dropdown, MultiselectDropdown, TextInput } from '@components/common';
import { useSiteAllDetailsStore } from '@store/siteAllDetailsStore';
import { onImageError } from '@helper/utils';
import { useGetRxDb } from '@hooks/getRxdbData';
import { AvailableProductsList } from '@services/hooks/site-leads';
import { DropdownOptions } from '@components/common/formControl/dropdown';
import { useComponentLevelAccessCheck } from '../../authGuard/hooks/useComponentLevelAccessCheck';

export const SiteVisit = (): ReactElement => {
	const { visit } = useSiteAllDetailsStore();
	const { result: availableProductsDataList } = useGetRxDb<AvailableProductsList>('products');
	const { result: availableOtherProductsDataList } = useGetRxDb<AvailableProductsList>('other_products');

	const hasFactoryDetailsAccess = useComponentLevelAccessCheck('factory-details');
	const availableProductsList = useMemo((): DropdownOptions[] => {
		if (availableProductsDataList?.length) {
			return availableProductsDataList.map((product, index) => ({
				id: index,
				name: product?.productgroup3description_c,
				value: product?.prdgroup3_c ?? '',
			}));
		}
		return [];
	}, [availableProductsDataList]);

	const availableProductsAtSite = useMemo(() => {
		const idArray = visit?.product_available_at_site?.split(',');
		return availableProductsList.filter((product) => idArray?.includes(product.value));
	}, [availableProductsList, visit?.product_available_at_site]);

	const availableOtherProductsList = useMemo((): DropdownOptions[] => {
		if (availableOtherProductsDataList?.length) {
			return availableOtherProductsDataList.map((product, index) => ({
				id: index,
				name: product?.productgroup3description_c,
				value: product?.prdgroup3_c ?? '',
			}));
		}
		return [];
	}, [availableOtherProductsDataList]);

	const availableOtherProductsDemoed = useMemo(() => {
		if (visit?.other_products_demoed_c === 'NA') return [{ id: availableOtherProductsList.length - 1, value: 'NA' }];
		const idArray = visit?.other_products_demoed_c?.split(',');
		return availableOtherProductsList.filter((product) => idArray?.includes(product.value));
	}, [visit?.other_products_demoed_c, availableOtherProductsList]);

	const availableOtherProductsDiscussed = useMemo(() => {
		if (visit?.other_products_discussed_c === 'NA') {
			return [{ id: availableOtherProductsList.length - 1, value: 'NA' }];
		}
		const idArray = visit?.other_products_discussed_c?.split(',');
		return availableOtherProductsList.filter((product) => idArray?.includes(product.value));
	}, [visit?.other_products_discussed_c, availableOtherProductsList]);

	const images = useMemo(
		(): string[] =>
			Array.from({ length: 5 }, (_, i) => {
				const key = `visit_image${i + 1}` as keyof typeof visit;
				return visit?.[key] as string | undefined;
			}).filter((img): img is string => Boolean(img)),
		[visit]
	);

	const demoedProducts = useMemo(() => {
		return [visit?.product1_demoed_name, visit?.product2_demoed_name].filter((product) => product).join(', ');
	}, [visit?.product1_demoed_name, visit?.product2_demoed_name]);

	const discussedProducts = useMemo(() => {
		return [visit?.product1_discussed, visit?.product2_discussed]
			.filter((product) => product)
			.map((product) => {
				if (!product) return product;

				const firstHyphenIndex = product.indexOf('-');
				if (firstHyphenIndex === -1) return product;

				return product.substring(firstHyphenIndex + 1);
			})
			.join(', ');
	}, [visit?.product1_discussed, visit?.product2_discussed]);

	const hasJoineryData = useMemo(() => {
		return !!(visit?.joinery_name_response || visit?.joinery_phone_number_response || visit?.joinery_address_response);
	}, [visit?.joinery_name_response, visit?.joinery_phone_number_response, visit?.joinery_address_response]);

	const hasFactoryData = useMemo(() => {
		return !!(visit?.factory_name_response || visit?.factory_phone_number_response || visit?.factory_address_response);
	}, [visit?.factory_name_response, visit?.factory_phone_number_response, visit?.factory_address_response]);

	const yesNoOptions: DropdownOptions[] = [
		{ id: 1, value: 'Yes' },
		{ id: 2, value: 'No' },
	];
	return (
		<section className="new-site-section">
			<div className="container">
				<form>
					<ul className="new-site-wrapper row">
						<li className="form-control d-col d-col-2" title={demoedProducts ?? ''}>
							<TextInput
								id="demoedProducts"
								name="demoedProducts"
								type="text"
								value={demoedProducts ?? ''}
								label="Product Demoed"
								isAutoFocus
								disabled
							/>
						</li>
						<li className="form-control d-col d-col-2" title={discussedProducts ?? ''}>
							<TextInput
								id="discussedProducts"
								name="discussedProducts"
								type="text"
								value={discussedProducts ?? ''}
								label="Product Discussed"
								disabled
							/>
						</li>

						<li className="d-col d-col-2 site-product-images-wrapper">
							<div className="upload-images-wrapper">
								{images?.map((image: string) => (
									<div key={`${image}`} className="upload-image">
										<div className="wrapper-upload-images">
											<img
												role="presentation"
												src={image}
												alt={image}
												title="site"
												width="56"
												height="56"
												onError={onImageError}
											/>
										</div>
									</div>
								))}
							</div>
						</li>
						<li className="d-col d-col-2">
							<MultiselectDropdown
								id="availableProducts"
								name="availableProducts"
								label="Product Available at Site"
								className="input-border single-lable-multi-dropdown"
								values={availableProductsAtSite}
								options={availableProductsList}
								required
								disabled
							/>
						</li>
						<li className="d-col d-col-2">
							<MultiselectDropdown
								id="availableOtherProductsDemoed"
								name="availableOtherProductsDemoed"
								label="Other Products Demoed"
								className="input-border single-lable-multi-dropdown"
								values={availableOtherProductsDemoed}
								options={availableOtherProductsList}
								required
								disabled
							/>
						</li>
						<li className="d-col d-col-2">
							<MultiselectDropdown
								id="availableOtherProductsDiscussed"
								name="availableOtherProductsDiscussed"
								label="Other Products Discussed"
								className="input-border single-lable-multi-dropdown"
								values={availableOtherProductsDiscussed}
								options={availableOtherProductsList}
								required
								disabled
							/>
						</li>
						<li
							className="form-control d-col d-col-2"
							title={visit?.product1_demoed_mng_c && visit?.reason_for_using_competition_c ? 'Yes' : 'No'}
						>
							<TextInput
								id="productComplaintLast1Month"
								name="productComplaintLast1Month"
								type="text"
								value={visit?.product1_demoed_mng_c && visit?.reason_for_using_competition_c ? 'Yes' : 'No'}
								label="Have you faced any product concern in last one month ?"
								isAutoFocus
								required
								disabled
							/>
						</li>
						{visit?.product1_demoed_mng_c && visit?.reason_for_using_competition_c ? (
							<>
								<li className="form-control d-col d-col-2" title={visit?.product1_demoed_mng_c ?? ''}>
									<TextInput
										id="complaintProductsList"
										name="complaintProductsList"
										type="text"
										value={visit?.product1_demoed_mng_c ?? ''}
										label="Product Name"
										isAutoFocus
										required
										disabled
									/>
								</li>
								<li className="form-control d-col d-col-2" title={visit?.reason_for_using_competition_c ?? ''}>
									<TextInput
										id="complaintProductsList"
										name="complaintProductsList"
										type="text"
										value={visit?.reason_for_using_competition_c ?? ''}
										label="Defect Name / Problem Description"
										isAutoFocus
										required
										disabled
									/>
								</li>
							</>
						) : null}
						<li className="d-col">
							<span className="divider-border"></span>
						</li>
						<li className="d-col d-col-2 ">
							<Dropdown
								id="isWeatherWorkGivenToJoinery"
								name="isWeatherWorkGivenToJoinery"
								label="Whether work is given to a Joinery?"
								title={hasJoineryData ? 'Yes' : 'No'}
								options={yesNoOptions}
								disabled
							/>
						</li>

						{hasJoineryData && (
							<>
								<li className="form-control d-col d-col-2">
									<TextInput
										id="joineryName"
										name="joineryName"
										type="text"
										value={visit?.joinery_name_response ?? ''}
										label="Name"
										disabled
									/>
								</li>
								<li className="form-control d-col d-col-2">
									<TextInput
										id="joineryNumber"
										name="joineryNumber"
										type="text"
										value={visit?.joinery_phone_number_response ?? ''}
										label="Joinery Number"
										disabled
									/>
								</li>
								<li className="form-control d-col d-col-2">
									<TextInput
										id="joineryAddress"
										name="joineryAddress"
										type="text"
										value={visit?.joinery_address_response ?? ''}
										label="Joinery Address"
										disabled
									/>
								</li>
							</>
						)}

						{!hasFactoryDetailsAccess && (
							<li className="d-col d-col-2 ">
								<Dropdown
									id="isWorkGivenToFactory"
									name="isWorkGivenToFactory"
									label="Whether work is given to a Factory?"
									title={hasFactoryData ? 'Yes' : 'No'}
									options={yesNoOptions}
									disabled
								/>
							</li>
						)}

						{hasFactoryData && (
							<>
								<li className="form-control d-col d-col-2">
									<TextInput
										id="factoryName"
										name="factoryName"
										type="text"
										value={visit?.factory_name_response ?? ''}
										label="Factory Name"
										disabled
									/>
								</li>
								<li className="form-control d-col d-col-2">
									<TextInput
										id="factoryNumber"
										name="factoryNumber"
										type="text"
										value={visit?.factory_phone_number_response ?? ''}
										label="Factory Number"
										disabled
									/>
								</li>
								<li className="form-control d-col d-col-2">
									<TextInput
										id="factoryAddress"
										name="factoryAddress"
										type="text"
										value={visit?.factory_address_response ?? ''}
										label="Factory Address"
										disabled
									/>
								</li>
							</>
						)}
					</ul>
				</form>
			</div>
		</section>
	);
};
